import { inject, Injectable, isDevMode } from '@angular/core';
import {
  GetRowIdParams,
  GridApi,
  GridOptions,
  GridReadyEvent,
  IRowNode,
  ITooltipParams,
  ValueFormatterParams,
} from 'ag-grid-community';
import { GridService, ReaderObject } from './types';
import { GridDataSourceInfiniteService } from './data-source.infinite.service';

@Injectable()
export class GridInfiniteService<DM = unknown> implements GridService<DM> {
  private readonly gridSources = inject<GridDataSourceInfiniteService<DM>>(GridDataSourceInfiniteService);
  private api?: GridApi;
  total$ = this.gridSources.total$;

  onGridReady(callback: unknown, event: GridReadyEvent): void {
    const { api } = event;
    this.api = api;
    // this.gridDataSources.resetReader()

    this.gridSources.requestStatus$.subscribe((status) => {
      if (status === 'before') {
        this.showLoadingOverlay();
      }
      if (['success', 'error'].includes(status)) {
        this.hideOverlays();
      }
    });

    this.gridSources.dataChange$.subscribe((rows) => {
      rows?.forEach((row) => {
        this.updateItem(row.id, row);
      });
    });

    if (callback && typeof callback === 'function') {
      callback(event);
    }
  }

  updateItem(id: number | string, data: ReaderObject<DM>): void {
    this.handleAPINotReady();
    const row: IRowNode | undefined = this.api?.getRowNode(id as string);
    if (row && row !== null) {
      const input = {
        ...row.data,
        ...data,
      };
      row.updateData(input);
      this.api?.refreshCells({
        rowNodes: [input],
        force: true,
      });
      this.gridSources.addReader([input]);
    }
  }

  showLoadingOverlay(): void {
    this.handleAPINotReady();
    this.api?.setGridOption('loading', true);
  }

  hideOverlays(): void {
    this.handleAPINotReady();
    this.api?.hideOverlay();
  }

  onFilterChanged(): void {
    this.handleAPINotReady();
    this.api?.onFilterChanged();
  }

  getOptions(options: GridOptions): GridOptions {
    const { onGridReady, ...config } = options;
    return {
      onGridReady: this.onGridReady.bind(this, onGridReady),
      datasource: this.gridSources,
      // loadingOverlayComponent:LoadingOverlayComponent
      getRowId: this.getRowId,
      rowModelType: 'infinite',
      cacheBlockSize: 50, //page size
      tooltipShowDelay: 0,
      defaultColDef: {
        flex: 1,
        minWidth: 100,
        sortable: true,
        suppressHeaderMenuButton: true,
        suppressMovable: true,
        headerClass: 'caption',
        valueFormatter: ({ value }: ValueFormatterParams) => value,
        tooltipValueGetter: ({ valueFormatted }: ITooltipParams) => valueFormatted,
        ...config.defaultColDef,
      },
      ...config,
    };
  }

  updateGrid(): void {
    this.gridSources.resetReader();
    this.api?.onFilterChanged();
  }

  refreshCache(): void {
    this.api?.refreshInfiniteCache();
  }

  deselectNodeById(id: string): void {
    const deselectNode = this.api?.getSelectedNodes().find((row) => row.id === id);
    if (deselectNode) {
      deselectNode.setSelected(false);
    }
  }

  private getRowId(node: GetRowIdParams): string {
    return node.data.id;
  }

  private handleAPINotReady(): void {
    if (!this.api && isDevMode()) {
      throw new Error('GridService: Grid is Not ready yet!');
    }
  }
}
